import type { GarmentMarkersType, PanelPlacementType } from 'src/types/decorationSpecs';

/**
 * Computes a suggested artwork placement from 5 designer-clicked landmarks on a flat garment
 * photo, DIS's decoration-placement data for one panel, and the product's resolved body
 * width/length in millimeters for the current size. Pure functions only; no canvas/UI/Redux
 * logic here, so the placement geometry can be validated in isolation.
 *
 * Core geometry:
 * 1. Works in pixels-per-mm: `decorationPlacement` fields and resolved body measurements are
 *    both in millimeters.
 * 2. The scale factor (px/mm) can be derived from either marker axis: the vertical pair
 *    (`shoulderTopY`/`garmentBottomY`, against body length) or the horizontal pair
 *    (`leftX`/`rightX`, against body width). Height mode is the default, as it holds up better
 *    on mannequin photos where curvature narrows apparent width more than length.
 */

export type ScaleAxis = 'height' | 'width';

export const DEFAULT_SCALE_AXIS: ScaleAxis = 'height';

/** Reason a placement couldn't be computed — structured so callers (the UI) can map each to their
 * own message rather than parsing prose. */
export type AutoPositionFailureReason =
    | 'no-panel-data'
    | 'no-body-width'
    | 'no-body-length'
    | 'invalid-body-width'
    | 'invalid-body-length'
    | 'invalid-markers';

export type AutoPositionRectangleType = {
    x: number;
    y: number;
    width: number;
    height: number;
    /**
     * True when the computed rectangle falls at least partially above `shoulderTopY` or below
     * `garmentBottomY`. `decorationPlacement` width/height/offset are trusted regardless —
     * this is advisory-only (so the UI can warn the designer) rather than withholding placement.
     */
    outsideGarmentBounds: boolean;
};

export type AutoPositionResultType =
    | ({ ok: true } & AutoPositionRectangleType)
    | { ok: false; reason: AutoPositionFailureReason };

export type ComputeAutoPositionParams = {
    markers: GarmentMarkersType;
    /** The single panel (e.g. "Front") the placement is being computed for — `undefined`/`null`
     * when DIS has no decorationPlacement entry for the surface the designer is placing on. */
    panel: PanelPlacementType | null | undefined;
    /** Already resolved-to-mm body width/length for the current size —
     * `undefined` when the product's size-chart data doesn't have a usable value for the
     * requested axis (see `getGarmentWarpAvailability`'s own "size chart data" check for the
     * coarser upstream gate). Resolution itself (matching a product's own attribute-model
     * properties by name, per `productPropertySizeChart.ts`'s `BODY_WIDTH_PATTERNS`/
     * `BODY_LENGTH_PATTERNS`) deliberately lives outside this module — this stays a pure function
     * over plain numbers, with no opinion on where a body measurement comes from. */
    bodyWidthMm?: number;
    bodyLengthMm?: number;
    scaleAxis?: ScaleAxis;
};

type ScaleResult = { ok: true; pxPerMm: number } | { ok: false; reason: AutoPositionFailureReason };

/**
 * `Math.abs`/min-max normalization is the decided behavior for markers placed "in reverse order"
 * (e.g. `rightX < leftX`) — rather than throwing. A designer's click order has no bearing on which
 * edge is physically which; the scale factor and centerline are computed the same either way, and
 * `garmentCenterX`'s average is already order-independent.
 */
function resolvePxPerMm(
    markers: GarmentMarkersType,
    bodyWidthMm: number | undefined,
    bodyLengthMm: number | undefined,
    scaleAxis: ScaleAxis,
): ScaleResult {
    if (scaleAxis === 'width') {
        if (bodyWidthMm === undefined) {
            return { ok: false, reason: 'no-body-width' };
        }
        if (bodyWidthMm <= 0 || Number.isNaN(bodyWidthMm)) {
            return { ok: false, reason: 'invalid-body-width' };
        }
        const markerSpan = Math.abs(markers.rightX - markers.leftX);
        if (markerSpan === 0) {
            return { ok: false, reason: 'invalid-markers' };
        }
        return {
            ok: true,
            pxPerMm: markerSpan / bodyWidthMm,
        };
    }

    if (bodyLengthMm === undefined) {
        return { ok: false, reason: 'no-body-length' };
    }
    if (bodyLengthMm <= 0 || Number.isNaN(bodyLengthMm)) {
        return { ok: false, reason: 'invalid-body-length' };
    }
    const markerSpan = Math.abs(markers.garmentBottomY - markers.shoulderTopY);
    if (markerSpan === 0) {
        return { ok: false, reason: 'invalid-markers' };
    }
    return {
        ok: true,
        pxPerMm: markerSpan / bodyLengthMm,
    };
}

export function computeAutoPosition({
    markers,
    panel,
    bodyWidthMm,
    bodyLengthMm,
    scaleAxis = DEFAULT_SCALE_AXIS,
}: ComputeAutoPositionParams): AutoPositionResultType {
    if (!panel) {
        return { ok: false, reason: 'no-panel-data' };
    }

    const scaleResult = resolvePxPerMm(markers, bodyWidthMm, bodyLengthMm, scaleAxis);
    if (!scaleResult.ok) {
        return scaleResult;
    }
    const { pxPerMm } = scaleResult;

    const width = panel.scaledPrintWidthMm * pxPerMm;
    const height = panel.scaledPrintHeightMm * pxPerMm;

    // Vertical: a fixed collar-to-artwork-top offset from decorationPlacement, applied below the
    // clicked collar point.
    const y = markers.collarBottomY + panel.distanceFromCollarToTopMm * pxPerMm;

    // Horizontal: garment centerline from the two clicked edges, offset by the panel's x-offset.
    const garmentCenterX = (markers.leftX + markers.rightX) / 2;
    const x = garmentCenterX - width / 2 + panel.xOffsetFromCenterMm * pxPerMm;

    const topBound = Math.min(markers.shoulderTopY, markers.garmentBottomY);
    const bottomBound = Math.max(markers.shoulderTopY, markers.garmentBottomY);
    const outsideGarmentBounds = y < topBound || y + height > bottomBound;

    return { ok: true, x, y, width, height, outsideGarmentBounds };
}
